import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "./use-auth";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/errors";
import { ClaimTask, AssignableUser } from "@/api/generated/client/models";

export function useTasks(claimId: number) {
    const { firebaseUser } = useAuth();
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data: tasks, isLoading, error } = useQuery({
        queryKey: ["claim-tasks", claimId],
        queryFn: async () => {
            if (!claimId) return [];
            const response = await apiClient.tasks.getClaimTasks({ claimId, includeCompleted: true });
            return response.items || [];
        },
        enabled: !!claimId && !!firebaseUser,
    });

    // Query for assignable users (users with access to this claim)
    const { data: assignableUsersData, isLoading: isLoadingAssignableUsers } = useQuery<AssignableUser[]>({
        queryKey: ["task-assignees", claimId],
        queryFn: async () => {
            if (!claimId) return [];
            const response = await apiClient.tasks.getTaskAssignees({ claimId });
            return response.data || [];
        },
        enabled: !!claimId && !!firebaseUser,
    });

    // Sort tasks: incomplete tasks first, then by priority, then by due date
    const sortedTasks = useMemo(() => {
        if (!tasks) return [];
        
        return [...tasks].sort((a, b) => {
            // Completed tasks go to bottom
            if (a.status === 'completed' && b.status !== 'completed') return 1;
            if (a.status !== 'completed' && b.status === 'completed') return -1;
            
            // Within same status, sort by priority (urgent > high > normal > low)
            const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
            const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 2;
            const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 2;
            if (aPriority !== bPriority) return aPriority - bPriority;
            
            // Finally sort by due date (earliest first)
            if (a.dueDate && b.dueDate) {
                return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            }
            if (a.dueDate) return -1;
            if (b.dueDate) return 1;
            return 0;
        });
    }, [tasks]);

    const createTaskMutation = useMutation({
        mutationFn: async (task: Partial<ClaimTask>) => {
            // Ensure we have a valid request object. Casting to any to satisfy generated types if strict
            return await apiClient.tasks.createClaimTask({
                claimId,
                createClaimTaskRequest: task as any
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-tasks", claimId] });
            // Also invalidate activities since creating a task creates an activity
            queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
    });

    const updateTaskMutation = useMutation({
        mutationFn: async ({ taskId, updates }: { taskId: number; updates: Partial<ClaimTask> }) => {
            return await apiClient.tasks.updateClaimTask({
                claimId,
                id: taskId,
                updateClaimTaskRequest: updates as any
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-tasks", claimId] });
            queryClient.invalidateQueries({ queryKey: ["tasks", "all"] });
            queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
            // Invalidate claim data since task completion may trigger status changes
            queryClient.invalidateQueries({ queryKey: ["claims", claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
    });

    const deleteTaskMutation = useMutation({
        mutationFn: async (taskId: number) => {
            await apiClient.tasks.deleteClaimTask({ claimId, id: taskId });
            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-tasks", claimId] });
            queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to delete task", description: message, variant: "destructive" });
        },
    });

    return {
        tasks: sortedTasks,
        isLoading,
        error,
        assignableUsers: assignableUsersData || [],
        isLoadingAssignableUsers,
        createTask: createTaskMutation.mutate,
        updateTask: updateTaskMutation.mutate,
        deleteTask: deleteTaskMutation.mutate,
        deleteTaskAsync: deleteTaskMutation.mutateAsync,
        isCreating: createTaskMutation.isPending,
        isUpdating: updateTaskMutation.isPending,
        isDeleting: deleteTaskMutation.isPending
    };
}
